'use client'
import type { FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import type { TypeLang } from '@/schema'
import { innerGrid, mq, presetsFluid, rgbAlpha, vw } from '@/styles'
import { halfScreenGrid } from './HeaderBrands'
import { Medium } from '@/components/base'

const mediumFullWidthDataBySlug = {
  cose: {
    type: 'video' as const,
    src: '/media/brands/cose/header.mp4',
    alt: 'header video cose'
  },
  ladecor: {
    type: 'video' as const,
    src: '/media/brands/ladecor/header.mp4',
    alt: 'header video ladecor'
  },
  softy: {
    type: 'video' as const,
    src: '/media/brands/softy/header.mp4',
    alt: 'header video softy'
  }
}

const headingId = 'brand-description'
export const SectionBrandDescription:FC = () => {
  const { slug } = useParams() as { slug: 'cose' | 'softy' | 'ladecor', lang: TypeLang }
// slug: keyof typeof mediumFullWidthDataBySlug }

	const mediumVideo = mediumFullWidthDataBySlug[slug]

  const { t } = useTranslator()

  return (
    <Section aria-labelledby={headingId}>
			<div className='medium-wrapper'>
      	<Medium data={mediumVideo} className='medium' />
			</div>
      <hgroup>
        <h2 id={headingId}>{t(`project.brands.${slug}.title`)}</h2>
        <p>{t(`project.brands.${slug}.description`)}</p>
      </hgroup>
    </Section>
  )
}

const Section = styled.section`
  align-items: center;
  background-color: var(--color-background--dark);
  color: var(--color-text--dark);
  display: flex;
  flex-direction: column;
  min-height: ${vw(508, 'mobile')};
  padding: ${vw(20, 'mobile')} ${vw(10, 'mobile')};
  width: var(--viewport-width);
  width: var(--viewport-width--dynamic);

  ${mq.greaterThan('nexus7')} {
    min-height: ${vw(508, 'tablet')};
    padding: ${vw(20, 'tablet')} ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: nowrap;
    min-height: ${vw(400, 'tablet')};
    padding: 0;
  }

  ${mq.greaterThan('desktop')} {
    min-height: 650px;
    padding: 0 ${vw(30, 'desktop')};
  }

  .medium-wrapper,
  hgroup {
    ${mq.greaterThan('tablet')} {
      ${innerGrid(halfScreenGrid.tablet!)};
      padding: ${vw(30, 'desktop')} ${vw(35, 'desktop')};
      width: 50vw !important;
      width: 50dvw !important;
    }

    ${mq.greaterThan('desktop-xs')} {
      width: var(--viewport-width-half) !important;
      width: var(--viewport-width-half--dynamic) !important;
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(halfScreenGrid.desktop!)};
      padding: 30px;
    }
  }

	.medium-wrapper {
		${mq.greaterThan('tablet')} {
			align-self: stretch;
		}

		video,
    img {
			grid-column: 1 / span 6;
			height: 100%;
			max-width: 100%;
      object-fit: cover;
      width: auto;
    }
  }

  hgroup {
    ${mq.greaterThan('tablet')} {
      border-left: 1px solid ${rgbAlpha('--color-text--dark', .2)};
      height: 100%;
      min-height: ${vw(400, 'tablet')};
    }

    ${mq.greaterThan('desktop')} {
      min-height: 650px;
    }
  }

  h2, p {
    text-align: left;
  }

  h2 {
    ${presetsFluid.font25_30()}
    grid-column: 1 / span 6;
    margin-bottom: ${vw(90, 'mobile')};
    margin-top: ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(90, 'tablet')};
      margin-top: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(90, 'desktop')};
			margin-top: 0;
    }

    ${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
      margin-bottom: 90px;
    }
  }

  p {
    ${presetsFluid.font18()}
    grid-column: 1 / span 6;
    margin-bottom: ${vw(30, 'mobile')};
    opacity: .7;

    ${mq.greaterThan('nexus7')} {
      grid-column: 1 / span 3;
      margin-bottom: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
      margin-top: auto;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font18_rel()}
		}
  }
`
